import axios from 'axios';
import { Project, ProjectSummary, Sensor, Obstacle, GridParams, SimulationTask, SimulationStatus, OtpRequest, TokenRequest, Token, User, PcdBundleUploadResponse, AlignmentConnectivityRequest, AlignmentConnectivityResponse, AutoFloorRequest, AutoFloorResponse, IcpAlignRequest, IcpAlignResponse, QortexSyncReport, QortexSyncResponse, SensorPointCloudManifestResponse, SensorPointCloudStatusResponse } from "./models";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
});

// Add a request interceptor to include the auth token in headers
apiClient.interceptors.request.use(
  (config) => {
    // Assuming the token is stored in localStorage after login
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// User status polling for immediate logout on deactivation
let userStatusPollingInterval: NodeJS.Timeout | null = null;

const startUserStatusPolling = () => {
  // Only start polling if user is logged in
  const token = localStorage.getItem('accessToken');
  if (!token) return;

  // Polling started

  // Clear any existing polling
  if (userStatusPollingInterval) {
    clearInterval(userStatusPollingInterval);
  }

  // Poll every 60 seconds to check user status
  userStatusPollingInterval = setInterval(async () => {
    try {
      await apiClient.get('/api/v1/users/me');
    } catch (error: any) {
      // Polling detected an auth error
      if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 404) {
        // User was deactivated, token expired, or user not found
        // User deactivated - logging out immediately
        localStorage.removeItem('accessToken');
        clearInterval(userStatusPollingInterval!);
        userStatusPollingInterval = null;

        // Show user-friendly message
        const errorMessage = error.response?.data?.detail || 'Your account has been deactivated. Please contact support.';

        // Dispatch a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('authError', {
          detail: { message: errorMessage, status: error.response?.status || 401 }
        }));
      }
    }
  }, 60000); // Poll every 60 seconds
};

const stopUserStatusPolling = () => {
  if (userStatusPollingInterval) {
    clearInterval(userStatusPollingInterval);
    userStatusPollingInterval = null;
  }
};

// Add a response interceptor to handle authentication errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/users/me') || error.config?.url?.includes('/login');
    const isRouteMiss = error.response?.status === 404
      && (error.response?.data?.code === 'route_miss'
        || error.response?.data?.detail === 'Not Found');
    if (isRouteMiss) {
      window.dispatchEvent(new CustomEvent('appVersionStale'));
    }

    const isAuthError = error.response?.status === 401
      || (error.response?.status === 404 && isAuthEndpoint)
      || (error.response?.status === 403 && (
        isAuthEndpoint
        || error.response?.data?.detail === 'Could not validate credentials'
      ));
    
    if (isAuthError) {
      // Token is invalid, expired, user is deactivated, or user not found
      localStorage.removeItem('accessToken');
      stopUserStatusPolling(); // Stop polling on logout

      // Show user-friendly message
      const errorMessage = error.response?.data?.detail || 'Your session has expired. Please log in again.';

      // Dispatch a custom event that components can listen to
      window.dispatchEvent(new CustomEvent('authError', {
        detail: { message: errorMessage, status: error.response?.status || 401 }
      }));

      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

interface Scene {
  sensors: Sensor[];
  obstacles: Obstacle[];
  projectId?: number | null;
}

export const getProjects = async (): Promise<ProjectSummary[]> => {
  const response = await apiClient.get('/api/v1/projects/');
  return response.data;
};

export const getProjectById = async (projectId: number): Promise<Project> => {
  const response = await apiClient.get(`/api/v1/projects/${projectId}`);
  return response.data;
};

export const createProject = async (name: string): Promise<Project> => {
  const response = await apiClient.post('/api/v1/projects/', { name });
  return response.data;
};

export const deleteProject = async (projectId: number): Promise<void> => {
  await apiClient.delete(`/api/v1/projects/${projectId}`);
};

export const duplicateProject = async (projectId: number): Promise<Project> => {
  const response = await apiClient.post(`/api/v1/projects/${projectId}/duplicate`);
  return response.data;
};


export const updateProject = async (project: Project): Promise<Project> => {
  const { id, ...data } = project;
  const response = await apiClient.put(`/api/v1/projects/${id}`, data);
  return response.data;
};

export const renameProject = async (projectId: number, name: string): Promise<Project> => {
  const response = await apiClient.put(`/api/v1/projects/${projectId}`, { name });
  return response.data;
};

export const exportProject = async (
  projectId: number,
  options?: { includeAlignmentScans?: boolean },
  onDownloadProgress?: (progress: { loaded: number; total: number; percent: number }) => void
): Promise<Blob> => {
  const params = new URLSearchParams();
  if (options?.includeAlignmentScans) {
    params.set('include_alignment_scans', 'true');
  }
  const query = params.toString();
  const response = await apiClient.get(
    `/api/v1/projects/${projectId}/export${query ? `?${query}` : ''}`,
    {
      responseType: 'blob',
      timeout: 30 * 60 * 1000,
      onDownloadProgress: onDownloadProgress
        ? (event) => {
            if (!event.total) return;
            onDownloadProgress({
              loaded: event.loaded,
              total: event.total,
              percent: Math.round((event.loaded / event.total) * 100),
            });
          }
        : undefined,
    }
  );
  return response.data;
};

export const PROJECT_IMPORT_DIRECT_MAX_BYTES = 28 * 1024 * 1024;

export type TransferProgress = { loaded: number; total: number; percent: number };

export const formatApiError = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean)
      .join(', ');
  }
  const status = error?.response?.status;
  if (status === 502 || status === 504) {
    return 'Import timed out at the gateway while processing on the server. Retry after infrastructure timeouts are increased, or try a smaller export.';
  }
  if (error?.message) {
    return error.message;
  }
  return fallback;
};

export const prepareProjectImport = async (
  filename: string,
  fileSize: number
): Promise<{ import_id: string; upload_url: string; content_type: string }> => {
  const formData = new FormData();
  formData.append('filename', filename);
  formData.append('file_size', String(fileSize));

  const response = await apiClient.post('/api/v1/projects/import/prepare', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

const uploadFileToSignedUrl = (
  uploadUrl: string,
  file: File,
  contentType: string,
  onUploadProgress?: (progress: TransferProgress) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onUploadProgress) return;
      onUploadProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.round((event.loaded / event.total) * 100),
      });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload failed due to a network error'));
    xhr.send(file);
  });

export const importProject = async (
  file: File,
  newName?: string,
  onUploadProgress?: (progress: TransferProgress) => void,
  onProcessing?: () => void
): Promise<Project> => {
  const useDirectUpload = file.size > PROJECT_IMPORT_DIRECT_MAX_BYTES;

  if (useDirectUpload) {
    try {
      const prepared = await prepareProjectImport(file.name, file.size);
      await uploadFileToSignedUrl(
        prepared.upload_url,
        file,
        prepared.content_type || 'application/zip',
        onUploadProgress
      );

      onProcessing?.();

      const directFormData = new FormData();
      directFormData.append('import_id', prepared.import_id);
      if (newName) {
        directFormData.append('new_name', newName);
      }

      const directResponse = await apiClient.post('/api/v1/projects/import', directFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30 * 60 * 1000,
      });
      return directResponse.data;
    } catch (error: any) {
      if (error.response?.status !== 404) {
        throw error;
      }
      // Local dev without GCS: fall back to multipart upload.
    }
  }

  const formData = new FormData();
  formData.append('file', file);
  if (newName) {
    formData.append('new_name', newName);
  }

  const response = await apiClient.post('/api/v1/projects/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30 * 60 * 1000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    onUploadProgress: onUploadProgress
      ? (event) => {
          if (!event.total) return;
          onUploadProgress({
            loaded: event.loaded,
            total: event.total,
            percent: Math.round((event.loaded / event.total) * 100),
          });
        }
      : undefined,
  });
  return response.data;
};

export const importQortexProject = async (file: File, newName?: string, sensorTypes?: string): Promise<Project> => {
  const formData = new FormData();
  formData.append('file', file);
  if (newName) {
    formData.append('new_name', newName);
  }
  if (sensorTypes) {
    formData.append('sensor_types', sensorTypes);
  }

  const response = await apiClient.post('/api/v1/projects/import-qortex', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export async function uploadReferenceImage(projectId: number, file: File): Promise<Project> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/reference-image`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data;
}

export async function downscaleReferenceImage(
  projectId: number,
  maxDimension: number,
): Promise<Project> {
  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/reference-image/downscale`,
    { max_dimension: maxDimension },
  );
  return response.data;
}

export async function uploadAlignmentPcdBundle(
  projectId: number,
  file: File,
  onUploadProgress?: (percent: number) => void
): Promise<PcdBundleUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/alignment/pcd-bundle`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Large archives (100MB+) need time to upload and process server-side.
      timeout: 30 * 60 * 1000,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (!event.total) return;
            onUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        : undefined,
    }
  );
  return response.data;
}

export async function deleteAlignmentScan(
  projectId: number,
  sensorId: string
): Promise<Project> {
  const response = await apiClient.delete(
    `/api/v1/projects/${projectId}/alignment/sensors/${sensorId}/scan`
  );
  return response.data;
}

export async function fetchAlignmentConnectivity(
  projectId: number,
  body: AlignmentConnectivityRequest
): Promise<AlignmentConnectivityResponse> {
  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/alignment/connectivity`,
    body
  );
  return response.data;
}

export async function autoFloorSensor(
  projectId: number,
  body: AutoFloorRequest
): Promise<AutoFloorResponse> {
  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/alignment/auto-floor`,
    body
  );
  return response.data;
}

export async function runIcpAlignment(
  projectId: number,
  body: IcpAlignRequest
): Promise<IcpAlignResponse> {
  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/alignment/icp`,
    body
  );
  return response.data;
}

export async function modifyQortexXml(projectId: number, file: File): Promise<Blob> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/alignment/modify-qortex`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    }
  );
  return response.data;
}

export async function previewQortexSync(
  projectId: number,
  file: File,
): Promise<QortexSyncReport> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/alignment/qortex-sync/preview`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data;
}

export async function applyQortexSync(
  projectId: number,
  file: File,
  sensorTypes: Record<string, string> = {},
): Promise<QortexSyncResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('sensor_types', JSON.stringify(sensorTypes));

  const response = await apiClient.post(
    `/api/v1/projects/${projectId}/alignment/qortex-sync/apply`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data;
}

export async function startHeatmapAnalysis(scene: Scene, targetParams: { width: number; height: number; depth: number; shape: string; ground_offset: number }, gridParams: GridParams): Promise<SimulationTask> {
  const response = await apiClient.post('/api/v1/simulations/heatmap', {
    scene,
    target_params: targetParams,
    grid_params: gridParams,
  });
  return response.data;
}

export const cancelHeatmapAnalysis = async (taskId: string): Promise<void> => {
  await apiClient.post(`/api/v1/simulations/cancel/${taskId}`);
};

export const checkHeatmapCache = async (projectId: number): Promise<{ has_cache: boolean, cache_key?: string, message?: string }> => {
  const response = await apiClient.get(`/api/v1/simulations/heatmap/cache/${projectId}`);
  return response.data;
};

export const loadCachedHeatmap = async (
  projectId: number, 
  cacheKey?: string, 
  gridWidth?: number, 
  gridHeight?: number,
  onProgress?: (loadedBytes: number, totalBytes?: number) => void
): Promise<{ heatmap: number[][], from_cache: boolean }> => {
  let url = `/api/v1/simulations/heatmap/cache/${projectId}/load`;
  const params = new URLSearchParams();
  if (cacheKey) params.append('cache_key', cacheKey);
  if (gridWidth) params.append('grid_width', gridWidth.toString());
  if (gridHeight) params.append('grid_height', gridHeight.toString());
  
  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  
  const response = await apiClient.get(url, {
    onDownloadProgress: (progressEvent) => {
      if (onProgress) {
        onProgress(progressEvent.loaded, progressEvent.total);
      }
    }
  });
  return response.data;
};

export const getAnalysisStatus = async (taskId: string): Promise<SimulationStatus> => {
  const response = await apiClient.get(`/api/v1/simulations/status/${taskId}`);
  return response.data;
};

export const requestOtp = async (email: string): Promise<void> => {
  const otpRequest: OtpRequest = { email };
  await apiClient.post('/api/v1/auth/otp', otpRequest);
};

export const getToken = async (email: string, otp: string): Promise<Token> => {
  const tokenRequest: TokenRequest = { email, otp };
  const response = await apiClient.post('/api/v1/auth/token', tokenRequest);
  return response.data;
};

export const logout = async (): Promise<void> => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    try {
      // Call backend logout endpoint to blacklist the token
      await apiClient.post('/api/v1/auth/logout', { token });
    } catch (error) {
      // Even if the logout call fails, we should still clear the local token
      console.warn('Failed to logout on server:', error);
    }
  }

  // Stop user status polling
  stopUserStatusPolling();

  // Clear local storage
  localStorage.removeItem('accessToken');
};

// Function to start polling after successful login
export const startPollingAfterLogin = () => {
  startUserStatusPolling();
};

// Function to start polling if user is already logged in (for page refresh)
export const startPollingIfLoggedIn = () => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    console.log('User already logged in - starting polling');
    startUserStatusPolling();
  }
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get('/api/v1/users/me');
  return response.data;
};

export const getUserProjectCount = async (): Promise<{ project_count: number }> => {
  const response = await apiClient.get('/api/v1/users/me/project-count');
  return response.data;
};

export const getUserTotalFileSize = async (): Promise<{ total_file_size_bytes: number }> => {
  const response = await apiClient.get('/api/v1/users/me/total-file-size');
  return response.data;
};

export const deleteCurrentUser = async (): Promise<{ message: string, user_id: number, email: string }> => {
  const response = await apiClient.delete('/api/v1/users/me');
  return response.data;
};

export const createSensorPointCloudManifest = async (
  scene: Scene,
  projectId?: number | null,
  signal?: AbortSignal,
): Promise<SensorPointCloudManifestResponse> => {
  const payload: Scene = {
    ...scene,
    projectId: projectId ?? scene.projectId ?? null,
  };
  const response = await apiClient.post('/api/v1/simulate/point-cloud/manifest', payload, { signal });
  return response.data;
};

export const getSensorPointCloudStatus = async (
  cacheKeys: string[],
  projectId?: number | null,
  signal?: AbortSignal,
): Promise<SensorPointCloudStatusResponse> => {
  const params = new URLSearchParams({ keys: cacheKeys.join(',') });
  if (projectId != null) {
    params.set('project_id', String(projectId));
  }
  const response = await apiClient.get(
    `/api/v1/simulate/point-cloud/clouds/status?${params.toString()}`,
    { signal },
  );
  return response.data;
};